import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ClickAwayListener,
} from "@material-ui/core";
import { Favorite, SentimentSatisfiedAlt } from "@material-ui/icons";
import { Send, ChevronDown, X, ChartLine, Pencil, Trash2 } from "lucide-react";
import { useHistory } from "react-router-dom";
import { useMediaQuery, useTheme } from "@material-ui/core";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import CommentText from "./comment-text";
import {
  sendCommentToPostAPI,
  updateGalleryCommentAPI,
  deleteGalleryCommentAPI,
  likeUserPostCommentAPI,
} from "../../api/userApi";
import { showToast } from "../../utils/toast";
import { checkRateLimit } from "../../utils/rateLimit";
import likeAnimStyle from "../../styles/likeAnimation";
import { useKeyboardScroll } from "../../hooks/useKeyboardScroll";
import VerifiedShieldIcon from "../../assets/VerifiedSupIcon";
import MoreVerticalIcon from "../../assets/MoreVerticalIcon";
import SendIconButton from "../../assets/images/icons/sendIconButton.svg";
import { formatPostDate } from "../../utils/timeFormatter";
import EllipsisMenuButton from "./EllipsisMenuButtons/EllipsisMenuButtonComment";

export interface Comment {
  id: string;
  replyToCommentId: string | null;
  text: string;
  hashtags?: string[];
  createdAt: string;
  isLiked: boolean;
  likesCount: number;
  user: {
    id: string;
    username: string;
    profileImg: string;
  };
  replies: Comment[];
  repliesCount: number;
  repliesCountAllWithNested?: number;
  status: string;
}

interface CommentsModalProps {
  open: boolean;
  postId: string;
  postUserId: string;
  currentUserId: string | null;
  postOwnerId: string;
  comments: Comment[];
  onCommentsChange: (comments: Comment[]) => void;
  onClose: () => void;
  onFetchComments: () => Promise<void>;
  mediaUrls: { [key: string]: string };
  loadingMedia: { [key: string]: boolean };
  onLoadReplies?: (commentId: string) => Promise<void>;
  RenderAvatar: (props: {
    profileImg: string;
    className: string;
    style?: React.CSSProperties;
  }) => React.ReactElement;
  isMobile?: boolean;
  classes: any;
  variant?: "mobile" | "desktop";
  currentUserProfileImg?: string;
  postOwnerUsername: string;
  postOwnerFullName: string;
  postOwnerProfileImg: string;
  description?: string;
  hashtags?: string[];
  createdAt?: string;
  autoScrollOnOpen?: boolean;
  isFeedPost?: boolean;
  hasMoreByPost?: boolean;
  handleLoadMoreComments?: () => void;
  /** Desktop header: show ⋮ menu (Insights / Edit / Delete) instead of close (X). */
  postOptionsMenu?: {
    onInsights?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
  };
  onCommentAdded?: () => void;
  commentsAllowed?: boolean;
}

const CommentsModal: React.FC<CommentsModalProps> = ({
  open,
  postId,
  postUserId,
  currentUserId,
  postOwnerId,
  comments,
  onCommentsChange,
  onClose,
  onFetchComments,
  mediaUrls,
  loadingMedia,
  onLoadReplies,
  RenderAvatar,
  isMobile = false,
  classes,
  variant = "desktop",
  currentUserProfileImg,
  postOwnerUsername,
  postOwnerFullName,
  postOwnerProfileImg,
  description,
  hashtags,
  createdAt,
  autoScrollOnOpen = false,
  isFeedPost = false,
  hasMoreByPost,
  handleLoadMoreComments,
  postOptionsMenu,
  onCommentAdded,
  commentsAllowed = true,
}) => {
  const theme = useTheme();
  const isMobileDevice =
    useMediaQuery(theme.breakpoints.down("xs")) || isMobile;
  const history = useHistory();
  const likeAnimation = likeAnimStyle();
  const inputRef = useRef<HTMLInputElement>(null);
  const commentsListRef = useRef<HTMLDivElement>(null);
  const [commentsListAtBottom, setCommentsListAtBottom] = useState(false);
  const [commentsListHasOverflow, setCommentsListHasOverflow] = useState(false);
  const lastCommentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { focusAndCenterInput } = useKeyboardScroll({
    enabled: isMobileDevice,
  });

  const [text, setText] = useState("");
  const [postOptionsAnchorEl, setPostOptionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [deletePostConfirmOpen, setDeletePostConfirmOpen] = useState(false);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [, setReplyToUsername] = useState<string | null>(null);
  const [showReplies, setShowReplies] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editText, setEditText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCommentLikeAnimationIndex, setShowCommentLikeAnimationIndex] =
    useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpenMenuMobile, setOpenMenuMobile] = useState(false);

  const handleTouchStart = () => {
    timerRef.current = setTimeout(() => {
      setOpenMenuMobile(true);
    }, 600);
  };

  const handleTouchEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatCommentTime = (createdAt: string): string => {
    const now = Date.now();
    const commentDate = new Date(createdAt).getTime();
    const diffMs = now - commentDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return "now";
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 4) return `${diffWeeks}w`;
    if (diffMonths < 12) return `${diffMonths}m`;
    return `${diffYears}y`;
  };

  const findInReplies = (replies: any[], targetId: string): boolean => {
    for (const r of replies) {
      if (r.id === targetId) return true;
      if (findInReplies(r.replies || [], targetId)) return true;
    }
    return false;
  };

  const findTopLevelParent = (
    commentsList: Comment[],
    targetId: string
  ): Comment | undefined => {
    for (const c of commentsList) {
      if (c.id === targetId) return c;
      if (findInReplies(c.replies || [], targetId)) return c;
    }
    return undefined;
  };

  const toggleReplies = (commentId: string, repliesCount: number) => {
    setShowReplies(prevState => {
      const isOpening = !prevState[commentId];
      // завантажуємо завжди при відкритті якщо є хоч один reply
      if (isOpening && repliesCount > 0) {
        onLoadReplies?.(commentId);
      }
      return { ...prevState, [commentId]: isOpening };
    });
  };

  const openEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setEditText(comment.text || "");
  };

  const handleSaveEditedComment = async () => {
    if (!editingComment) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      showToast("Comment text cannot be empty", "error");
      return;
    }
    try {
      setSavingComment(true);
      await updateGalleryCommentAPI(editingComment.id, {
        comment: trimmed,
      });
      onCommentsChange(
        comments.map(c => {
          if (c.id === editingComment.id) {
            return { ...c, text: trimmed };
          }
          if (c.replies.some(reply => reply.id === editingComment.id)) {
            return {
              ...c,
              replies: c.replies.map(reply =>
                reply.id === editingComment.id
                  ? { ...reply, text: trimmed }
                  : reply
              ),
            };
          }
          return c;
        })
      );
      showToast("Comment updated", "success");
      setEditingComment(null);
    } catch (err: any) {
      console.error("Error updating comment", err);
      showToast(err.message || "Error updating comment", "error");
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (
    commentId: string,
    isReply: boolean = false,
    parentCommentId?: string
  ) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    try {
      await deleteGalleryCommentAPI(commentId);

      const updatedComments =
        isReply && parentCommentId
          ? comments.map(c => {
              if (c.id === parentCommentId) {
                return {
                  ...c,
                  replies: c.replies.filter(reply => reply.id !== commentId),
                  repliesCount: c.repliesCount - 1,
                  repliesCountAllWithNested: c.repliesCountAllWithNested
                    ? c.repliesCountAllWithNested - 1
                    : 0,
                };
              }
              return c;
            })
          : comments.filter(c => c.id !== commentId);

      onCommentsChange(updatedComments);

      showToast("Comment deleted", "success");
    } catch (err) {
      console.error("Error deleting comment", err);
      showToast("Error deleting comment", "error");
    }
  };

  const handleCommentLike = async (
    commentId: string,
    postId: string,
    idx: number
  ) => {
    try {
      await likeUserPostCommentAPI(postId, commentId);
      onCommentsChange(
        comments.map((c, index) => {
          if (index === idx && c.id === commentId) {
            return {
              ...c,
              isLiked: !c.isLiked,
              likesCount: c.isLiked ? c.likesCount - 1 : c.likesCount + 1,
            };
          }
          const updatedReplies = c.replies.map(reply => {
            if (reply.id === commentId) {
              return {
                ...reply,
                isLiked: !reply.isLiked,
                likesCount: reply.isLiked
                  ? reply.likesCount - 1
                  : reply.likesCount + 1,
              };
            }
            return reply;
          });
          if (updatedReplies.some(r => r.id === commentId)) {
            return { ...c, replies: updatedReplies };
          }
          return c;
        })
      );
    } catch (error) {
      console.log(`Error liking comment: ${error}`);
      showToast("Error liking comment", "error");
    }
  };

  const handleSendComment = async () => {
    if (!text.trim()) return;

    const isPostOwner = Boolean(currentUserId && currentUserId === postOwnerId);
    if (!commentsAllowed && !isPostOwner) {
      showToast("Comments are available for subscribers only.", "info");
      return;
    }

    const key = `comment-${currentUserId || "guest"}`;
    const { allowed, retryIn } = checkRateLimit(key, 3, 60 * 1000);
    if (!allowed) {
      showToast(
        `Too many comments. Try again in ${Math.ceil(retryIn / 1000)}s`,
        "info"
      );
      return;
    }
    try {
      await sendCommentToPostAPI(text, {
        galleryPostId: postId,
        commentId: replyToId,
      });
      showToast("Comment sent");

      if (replyToId) {
        // знаходимо батьківський коментар верхнього рівня
        const parentComment = findTopLevelParent(comments, replyToId!);

        // оновлюємо repliesCount локально одразу
        onCommentsChange(
          comments.map(c =>
            c.id === parentComment?.id
              ? { ...c, repliesCount: (c.repliesCount || 0) + 1 }
              : c
          )
        );

        if (parentComment) {
          setTimeout(async () => {
            await onLoadReplies?.(parentComment.id);
            // відкриваємо replies після завантаження
            setShowReplies(prev => ({ ...prev, [parentComment.id]: true }));
          }, 500);
        }
      } else {
        if (isExpanded || !isFeedPost) {
          await onFetchComments();
        }
        onCommentAdded?.();
      }

      setText("");
      setReplyToId(null);
      setReplyToUsername(null);

      if (isMobileDevice && inputRef.current) {
        setTimeout(() => {
          focusAndCenterInput(inputRef);
        }, 200);
      } else {
        inputRef.current?.focus();
      }
      // await onFetchComments();
    } catch (error: any) {
      showToast(error.message || "Error sending comment", "error");
    }
  };

  const handleEmojiSelect = (emoji: any) => {
    const char = emoji?.native || "";
    const input = inputRef.current;
    if (!char) return;
    if (!input) {
      setText(prev => prev + char);
      setShowEmojiPicker(false);
      return;
    }
    const start = input.selectionStart ?? text.length;
    const end = input.selectionEnd ?? start;
    const next = `${text.slice(0, start)}${char}${text.slice(end)}`;
    setText(next);
    setShowEmojiPicker(false);
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + char.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  const handleUserClick = (username: string) => {
    history.push({
      pathname: `/${username}`,
      state: {
        fromPostDialog: true,
        postId: postId,
        userId: postUserId,
        previousPath: history.location.pathname + history.location.search,
      },
    });
    onClose();
  };

  // Знаходить username по id серед всіх коментарів і replies
  const findUsernameById = (commentId: string): string => {
    for (const comment of comments) {
      if (comment.id === commentId) return comment.user.username;
      for (const reply of comment.replies || []) {
        if (reply.id === commentId) return reply.user.username;
      }
    }
    return "";
  };

  const isMobileVariant = variant === "mobile";

  const hasPostMenuInsights = Boolean(postOptionsMenu?.onInsights);
  const hasPostMenuEdit = Boolean(postOptionsMenu?.onEdit);
  const hasPostMenuDelete = Boolean(postOptionsMenu?.onDelete);
  const hasAnyPostMenuItem =
    hasPostMenuInsights || hasPostMenuEdit || hasPostMenuDelete;
  const showPostOptionsDots = postOptionsMenu && hasAnyPostMenuItem;

  useEffect(() => {
    if (!open || !autoScrollOnOpen) return;
    // Defer until content mounts & layout is calculated.
    requestAnimationFrame(() => {
      const el = commentsListRef.current;
      if (!el) return;
      el.scrollTop = 0;
      el.scrollIntoView({ block: "start" });
    });
  }, [open, autoScrollOnOpen]);

  const updateCommentsListScrollState = useCallback(() => {
    const el = commentsListRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    setCommentsListHasOverflow(hasOverflow);
    setCommentsListAtBottom(atBottom);
  }, []);

  useEffect(() => {
    if (!open) return;
    // initial compute after render
    requestAnimationFrame(() => updateCommentsListScrollState());
  }, [open, comments, isExpanded, isFeedPost, updateCommentsListScrollState]);

  const visibleComments =
    isFeedPost && !isExpanded ? comments.slice(0, 1) : comments;

  // IntersectionObserver — works only after expanded
  useEffect(() => {
    if (!hasMoreByPost || !handleLoadMoreComments) return;

    if (isFeedPost && !isExpanded) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreByPost) {
          handleLoadMoreComments();
        }
      },
      { threshold: 0.5 }
    );

    if (lastCommentRef.current) {
      observer.observe(lastCommentRef.current);
    }

    return () => observer.disconnect();
  }, [
    isExpanded,
    hasMoreByPost,
    handleLoadMoreComments,
    visibleComments,
    isFeedPost,
  ]);

  useEffect(() => {
    setIsExpanded(false);
  }, [postId]);

  if (!open) return null;

  const ReplyComponent = ({ reply, c, idx }: any) => (
    <div key={reply.id} className={classes.commentItem}>
      <RenderAvatar
        profileImg={reply.user.profileImg}
        className={classes.smallAvatar}
      />
      <div className={classes.commentBody}>
        <Typography
          className={classes.commentAuthor}
          style={{
            textDecoration: "none",
            cursor: "pointer",
          }}
          onClick={() => handleUserClick(reply.user.username)}
        >
          {reply.user.username}
          {reply.replyToCommentId && reply.replyToCommentId !== c.id && (
            <span
              style={{
                color: "#A3A3A3",
                fontWeight: 400,
                fontSize: 14,
                paddingLeft: 8,
              }}
            >
              {" "}
              to {findUsernameById(reply.replyToCommentId)}
            </span>
          )}
        </Typography>
        <CommentText
          text={reply.text}
          className={classes.commentText}
          onMentionClick={() => handleUserClick(reply.user.username)}
        />
        <div className={classes.commentFooter}>
          <Typography variant="caption" className={classes.commentFooterText}>
            {formatCommentTime(reply.createdAt)}
          </Typography>
          <span className={classes.dot}></span>
          <Typography
            variant="caption"
            className={classes.commentReplyButton}
            onClick={() => {
              setReplyToUsername(reply.user.username);
              setReplyToId(reply.id);
              setText(`@${reply.user.username}, `);
              inputRef.current?.focus();
            }}
          >
            Reply
          </Typography>
        </div>
      </div>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <IconButton
          className={classes.likeBtn}
          onClick={() => handleCommentLike(reply.id, postId, idx)}
        >
          <Favorite
            fontSize="small"
            className={
              showCommentLikeAnimationIndex === reply.id
                ? likeAnimation.favoritePop
                : ""
            }
            style={{
              height: 20,
              width: 20,
              color: reply.isLiked ? "#FE5A97" : "transparent",
              stroke: reply.isLiked ? "none" : "white",
              strokeWidth: reply.isLiked ? 0 : 1,
              transition: "color 0.3s",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowCommentLikeAnimationIndex(reply.id);
              setTimeout(() => setShowCommentLikeAnimationIndex(reply.id), 0);
              setTimeout(() => setShowCommentLikeAnimationIndex(null), 500);
            }}
          />
        </IconButton>
        <Typography variant="caption" style={{ margin: 0, fontSize: 14 }}>
          {reply.likesCount === 0 ? "" : reply.likesCount}
        </Typography>
        <EllipsisMenuButton
          comment={reply}
          handleEdit={() => openEditComment(reply)}
          isOpenMenuMobile={isOpenMenuMobile}
          setOpenMenuMobile={setOpenMenuMobile}
        />
      </Box>
    </div>
  );

  return (
    <>
      <Box
        style={{
          width: isMobileVariant ? "100%" : "40%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          maxHeight: isMobileVariant ? "100%" : "calc(100vh - 129px)",
          overflow: "hidden",
          position: "relative",
          backgroundColor: isMobileVariant ? "transparent" : "#181615",
        }}
      >
        <div
          className={classes.detailsSection}
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            height: "100%",
          }}
        >
          {!isMobileVariant && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "12px 16px 4px 0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {/* <RenderAvatar
                  profileImg={postOwnerProfileImg}
                  className={classes.smallAvatar}
                  style={{ width: 32, height: 32 }}
                /> */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* <Typography
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {postOwnerFullName}
                    <VerifiedShieldIcon className={classes.verifiedIcon} />
                  </Typography> */}
                  <Typography
                    style={{
                      fontSize: "20px",
                      fontWeight: 500,
                      color: "#FF78AA",
                    }}
                  >
                    @{postOwnerUsername}
                  </Typography>
                  {description && (
                    <Typography className={classes.overlayDescription}>
                      {description}
                    </Typography>
                  )}
                  {hashtags && hashtags.length > 0 && (
                    <Box className={classes.overlayHashtags}>
                      {hashtags.map((tag, index) => (
                        <Typography
                          key={index}
                          className={classes.overlayHashtag}
                        >
                          #{tag}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {createdAt && (
                    <Typography
                      style={{
                        color: "#FFFFFFCC",
                        fontSize: "16px",
                        fontWeight: 400,
                        textAlign: "left",
                      }}
                    >
                      {formatPostDate(createdAt)}
                    </Typography>
                  )}
                </div>
              </div>
              {showPostOptionsDots ? (
                <>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      setPostOptionsAnchorEl(e.currentTarget);
                    }}
                    aria-label="post options"
                    style={{
                      color: "#FFFFFF",
                      padding: "8px",
                    }}
                  >
                    <MoreVerticalIcon width={18} height={18} />
                  </IconButton>
                  <Menu
                    anchorEl={postOptionsAnchorEl}
                    open={Boolean(postOptionsAnchorEl)}
                    onClose={() => setPostOptionsAnchorEl(null)}
                    elevation={8}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      style: {
                        backgroundColor: "#1a1a1a",
                        border: "1px solid #363636",
                        borderRadius: 12,
                        padding: 8,
                        minWidth: 200,
                        boxSizing: "border-box",
                      },
                    }}
                    MenuListProps={{
                      disablePadding: true,
                      style: { padding: 0 },
                    }}
                  >
                    {hasPostMenuInsights && (
                      <MenuItem
                        onClick={() => {
                          setPostOptionsAnchorEl(null);
                          postOptionsMenu!.onInsights?.();
                        }}
                        disableRipple
                        style={{
                          color: "#FFFFFF",
                          borderRadius: 8,
                          padding: "10px 12px",
                          marginBottom:
                            hasPostMenuEdit || hasPostMenuDelete ? 6 : 0,
                          backgroundColor: "#242424",
                          fontSize: 14,
                          fontWeight: 500,
                          fontFamily: "'Roboto', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          minHeight: 44,
                        }}
                      >
                        <ChartLine
                          size={18}
                          strokeWidth={1.75}
                          color="#FFFFFF"
                        />
                        Insights
                      </MenuItem>
                    )}
                    {hasPostMenuInsights &&
                      (hasPostMenuEdit || hasPostMenuDelete) && (
                        <Divider
                          style={{
                            margin: "0 0 6px 0",
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            height: 1,
                          }}
                        />
                      )}
                    {hasPostMenuEdit && (
                      <MenuItem
                        onClick={() => {
                          setPostOptionsAnchorEl(null);
                          postOptionsMenu?.onEdit?.();
                        }}
                        disableRipple
                        style={{
                          color: "#FFFFFF",
                          borderRadius: 8,
                          padding: "10px 12px",
                          marginBottom: hasPostMenuDelete ? 6 : 0,
                          backgroundColor: "#242424",
                          fontSize: 14,
                          fontWeight: 500,
                          fontFamily: "'Roboto', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          minHeight: 44,
                        }}
                      >
                        <Pencil size={18} strokeWidth={1.75} color="#FFFFFF" />
                        Edit
                      </MenuItem>
                    )}
                    {hasPostMenuEdit && hasPostMenuDelete && (
                      <Divider
                        style={{
                          margin: "0 0 6px 0",
                          backgroundColor: "rgba(255, 255, 255, 0.08)",
                          height: 1,
                        }}
                      />
                    )}
                    {hasPostMenuDelete && (
                      <MenuItem
                        onClick={() => {
                          setPostOptionsAnchorEl(null);
                          setDeletePostConfirmOpen(true);
                        }}
                        disableRipple
                        style={{
                          color: "#FE5A97",
                          borderRadius: 8,
                          padding: "10px 12px",
                          backgroundColor: "#242424",
                          fontSize: 14,
                          fontWeight: 500,
                          fontFamily: "'Roboto', sans-serif",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          minHeight: 44,
                        }}
                      >
                        <Trash2 size={18} strokeWidth={1.75} color="#FE5A97" />
                        Delete
                      </MenuItem>
                    )}
                  </Menu>
                </>
              ) : (
                <IconButton
                  onClick={onClose}
                  style={{
                    color: "#FFFFFF",
                    padding: "8px",
                  }}
                >
                  <X size={20} />
                </IconButton>
              )}
            </div>
          )}
          {comments.length > 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding:
                  isMobile && !isFeedPost ? "12px 16px" : "16px 16px 8px 0",
              }}
            >
              <Typography
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                }}
              >
                Comments:
              </Typography>
              {!isFeedPost && isMobileVariant && (
                <IconButton
                  onClick={onClose}
                  style={{
                    color: "#FFFFFF",
                    padding: "8px",
                  }}
                >
                  <X size={24} />
                </IconButton>
              )}
            </div>
          ) : (
            ""
          )}
          {comments.length === 0 ? (
            <div
              style={{
                fontSize: "12px",
                fontWeight: 400,
                color: "#A3A3A3",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: isFeedPost ? "flex-start" : "center",
              }}
            >
              <span>No comments yet. Be first to comment!</span>
            </div>
          ) : (
            <div className={classes.commentsListWrapper}>
              <div
                className={classes.commentsList}
                ref={commentsListRef}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: isFeedPost ? "12px 0 0 0" : "0 20px",
                  paddingBottom: isFeedPost ? 0 : 50,
                  maxHeight: isFeedPost ? "568px" : "calc(50vh - 84px)",
                }}
                onScroll={updateCommentsListScrollState}
              >
                {visibleComments.length > 0 &&
                  visibleComments.map((c, idx) => (
                    <div
                      key={c.id}
                      ref={
                        idx === visibleComments.length - 1
                          ? lastCommentRef
                          : null
                      }
                      onTouchStart={handleTouchStart}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div>
                        <div className={classes.commentItem}>
                          <RenderAvatar
                            profileImg={c.user.profileImg}
                            className={classes.smallAvatar}
                          />
                          <div className={classes.commentBody}>
                            <Typography
                              className={classes.commentAuthor}
                              style={{
                                textDecoration: "none",
                                cursor: "pointer",
                              }}
                              onClick={() => handleUserClick(c.user.username)}
                            >
                              {c.user.username}
                            </Typography>
                            <CommentText
                              text={c.text}
                              className={classes.commentText}
                              onMentionClick={() =>
                                handleUserClick(c.user.username)
                              }
                            />
                            <div className={classes.commentFooter}>
                              <Typography
                                variant="caption"
                                className={classes.commentFooterText}
                              >
                                {formatCommentTime(c.createdAt)}
                              </Typography>
                              <span className={classes.dot}></span>
                              <Typography
                                variant="caption"
                                className={classes.commentReplyButton}
                                onClick={() => {
                                  setReplyToUsername(c.user.username);
                                  setReplyToId(c.id);
                                  setText(`@${c.user.username}, `);
                                  if (isMobileDevice && inputRef.current) {
                                    focusAndCenterInput(inputRef);
                                  } else {
                                    inputRef.current?.focus();
                                  }
                                }}
                              >
                                Reply
                              </Typography>
                            </div>
                          </div>

                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <IconButton
                              className={classes.likeBtn}
                              onClick={() =>
                                handleCommentLike(c.id, postId, idx)
                              }
                            >
                              <Favorite
                                fontSize="small"
                                className={
                                  showCommentLikeAnimationIndex === c.id
                                    ? likeAnimation.favoritePop
                                    : ""
                                }
                                style={{
                                  height: 20,
                                  width: 20,
                                  color: c.isLiked ? "#FE5A97" : "transparent",
                                  stroke: c.isLiked ? "none" : "white",
                                  strokeWidth: c.isLiked ? 0 : 1,
                                  transition: "color 0.3s",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setShowCommentLikeAnimationIndex(c.id);
                                  setTimeout(
                                    () =>
                                      setShowCommentLikeAnimationIndex(c.id),
                                    0
                                  );
                                  setTimeout(
                                    () =>
                                      setShowCommentLikeAnimationIndex(null),
                                    500
                                  );
                                }}
                              />
                            </IconButton>
                            <Typography
                              variant="caption"
                              style={{ margin: 0, fontSize: 14 }}
                            >
                              {c.likesCount === 0 ? "" : c.likesCount}
                            </Typography>
                            <EllipsisMenuButton
                              comment={c}
                              handleEdit={() => openEditComment(c)}
                              isOpenMenuMobile={isOpenMenuMobile}
                              setOpenMenuMobile={setOpenMenuMobile}
                            />
                          </Box>
                        </div>
                        {c.repliesCount > 0 && c.replies.length > 0 && (
                          <div>
                            {showReplies[c.id] ? (
                              <>
                                <div
                                  style={{
                                    margin: isFeedPost
                                      ? "0px 0 12px 110px"
                                      : "0px 0 12px 56px",
                                  }}
                                >
                                  {c.replies.map(reply => (
                                    <React.Fragment key={reply.id}>
                                      <ReplyComponent
                                        reply={reply}
                                        c={c}
                                        idx={idx}
                                      />
                                      {reply.replies &&
                                        reply.replies.map(
                                          (nestedReply: any) => (
                                            <React.Fragment
                                              key={nestedReply.id}
                                            >
                                              <ReplyComponent
                                                reply={nestedReply}
                                                c={c}
                                                idx={idx}
                                              />
                                            </React.Fragment>
                                          )
                                        )}
                                    </React.Fragment>
                                  ))}
                                  <Box
                                    onClick={() =>
                                      toggleReplies(
                                        c.id,
                                        (c.repliesCountAllWithNested ??
                                          c.repliesCount ??
                                          c.replies.length) as number
                                      )
                                    }
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginTop: "4px",
                                      marginBottom: "4px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      style={{
                                        fontWeight: 700,
                                        color: "#FFD0E1",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Hide replies
                                    </Typography>
                                  </Box>
                                </div>
                              </>
                            ) : (
                              <Box
                                onClick={() =>
                                  toggleReplies(
                                    c.id,
                                    (c.repliesCountAllWithNested ??
                                      c.repliesCount ??
                                      c.replies.length) as number
                                  )
                                }
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  margin: "8px 0 8px 50px",
                                  cursor: "pointer",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{
                                    fontWeight: 700,
                                    color: "#FFD0E1",
                                    fontSize: "14px",
                                  }}
                                >
                                  {typeof c.repliesCountAllWithNested ===
                                  "number"
                                    ? `View ${c.repliesCountAllWithNested} ${
                                        c.repliesCountAllWithNested === 1
                                          ? "reply"
                                          : "replies"
                                      }`
                                    : "View replies"}
                                </Typography>
                                <ChevronDown
                                  size={20}
                                  style={{ color: "#FFFFFFCC" }}
                                />
                              </Box>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
              <div
                style={
                  !commentsListHasOverflow || commentsListAtBottom
                    ? {}
                    : {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "77px",
                        background:
                          "linear-gradient(to top, #181615, transparent)",
                        pointerEvents: "none",
                      }
                }
              />
            </div>
          )}

          {isFeedPost &&
            !isExpanded &&
            (hasMoreByPost || comments.length > 1) && (
              <Box
                onClick={() => {
                  setIsExpanded(true);
                  handleLoadMoreComments?.();
                }}
                style={{ cursor: "pointer" }}
              >
                <Typography
                  variant="caption"
                  style={{ color: "#FE5A97", fontWeight: 600 }}
                >
                  Show more
                </Typography>
              </Box>
            )}
        </div>

        <div
          className={classes.commentInputContainer}
          style={{
            flexShrink: 0,
            backgroundColor: isMobileVariant ? "#1F1F1F" : "#181615",
            borderRadius: 12,
            zIndex: 10,
            padding: isMobileVariant ? "4px 8px" : "0",
            marginTop: "16px",
          }}
        >
          <Box
            style={{
              position: "relative",
              width: "100%",
              backgroundColor: isMobileVariant ? "transparent" : "#1F1F1F",
              borderRadius: isMobileVariant ? "0" : "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* {isMobileVariant && currentUserProfileImg && (
              <RenderAvatar
                profileImg={currentUserProfileImg}
                className={classes.smallAvatar}
                style={{ width: "24px", height: "24px" }}
              />
            )} */}
            <InputBase
              className={classes.commentInput}
              placeholder="How do you like this post?"
              value={text}
              onChange={e => setText(e.target.value)}
              inputRef={inputRef}
              style={{
                flex: 1,
                color: "#FFFFFF",
                padding: isMobileVariant
                  ? "17px 80px 16px 8px"
                  : "17px 100px 16px 12px",
                fontSize: isMobileVariant ? "12px" : "14px",
              }}
              inputProps={{
                style: {
                  color: "#FFFFFF",
                  fontSize: 16,
                },
              }}
            />
            <IconButton
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              style={{
                position: "absolute",
                right: isMobileVariant ? "65px" : "70px",
                padding: "4px",
                color: isMobileVariant ? "#FE5A97" : "#FFFFFF",
              }}
            >
              <SentimentSatisfiedAlt
                style={{
                  fontSize: isMobileVariant ? "22px" : "20px",
                  color: "#FFFFFF",
                }}
              />
            </IconButton>
            {showEmojiPicker && (
              <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                <Box
                  style={{
                    position: "absolute",
                    bottom: "48px",
                    right: "18px",
                    zIndex: 1000,
                  }}
                >
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="dark"
                    searchPosition="sticky"
                  />
                </Box>
              </ClickAwayListener>
            )}
            {isMobileVariant ? (
              <Button
                disabled={!text.trim()}
                onClick={handleSendComment}
                className={classes.postButton}
                style={{ fontSize: "10px", padding: "12px 16px" }}
              >
                <img
                  src={SendIconButton}
                  alt="Send"
                  style={{ width: "24px", height: "24px" }}
                />
              </Button>
            ) : (
              <IconButton
                disabled={!text.trim()}
                onClick={handleSendComment}
                className={classes.postButton}
                style={{
                  position: "absolute",
                  right: 8,
                  padding: "14px 18px",
                  borderRadius: "6px",
                  color: "#FFFFFF",
                }}
              >
                <Send size={20} />
              </IconButton>
            )}
          </Box>
        </div>
      </Box>

      <Dialog
        open={!!editingComment}
        onClose={() => setEditingComment(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
          },
        }}
      >
        <DialogTitle style={{ color: "#fff" }}>Edit Comment</DialogTitle>
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <TextField
            label="Comment"
            multiline
            minRows={3}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            variant="outlined"
            fullWidth
            placeholder="Enter your comment..."
            className={classes.editPostTextField}
          />
        </DialogContent>
        <DialogActions
          style={{ padding: "16px 24px", borderTop: "1px solid #333" }}
        >
          <Button
            onClick={() => {
              setEditingComment(null);
            }}
            style={{ color: "#999" }}
            disabled={savingComment}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditedComment}
            style={{
              backgroundColor: "#FE5A97",
              color: "#fff",
            }}
            disabled={savingComment || !editText.trim()}
          >
            {savingComment ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletePostConfirmOpen}
        onClose={() => setDeletePostConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            backgroundColor: "#131313",
            color: "#fff",
            borderRadius: 12,
            border: "1px solid #363636",
          },
        }}
      >
        <DialogTitle style={{ color: "#FE5A97", fontSize: 18 }}>
          Delete post?
        </DialogTitle>
        <DialogContent>
          <Typography style={{ color: "#ccc", fontSize: 14 }}>
            Are you sure you want to delete this post? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions
          style={{ padding: "8px 16px 16px", justifyContent: "flex-end" }}
        >
          <Button
            onClick={() => setDeletePostConfirmOpen(false)}
            style={{ color: "#999", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeletePostConfirmOpen(false);
              postOptionsMenu?.onDelete?.();
            }}
            style={{
              backgroundColor: "#FE5A97",
              color: "#fff",
              textTransform: "none",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentsModal;
